import { useT } from '@kmgestao/i18n';
import { Badge, PageContainer, PageIntroCard } from '@kmgestao/ui';

import { useCurrentClinicId, useSession } from '../../../features/auth/index.js';
import { ClinicShell } from '../ClinicShell.js';

import { PlanStatusSummaryCard } from './PlanStatusSummaryCard.js';
import { QuickActionsCard } from './QuickActionsCard.js';

/**
 * Dashboard inicial da clínica (PR-M2-05) — substitui o placeholder
 * de debug que existia em `routes/dashboard/index.tsx` quando a rota
 * `/dashboard` cai sob role `clinic_*`.
 *
 * Conteúdo intencionalmente leve nesta primeira versão:
 *   - **Boas-vindas** com saudação personalizada
 *   - **Status do plano** (compacto — link pra `/plano` pra ver tudo)
 *   - **Atalhos** (grid de cards-link pras outras 5 áreas: perfil,
 *     plano, equipe, identidade visual, IA)
 *
 * Decisão consciente: NÃO incluir KPIs operacionais (atendimentos
 * agendados, novos pacientes, faturamento) nesta PR — esses cards
 * dependem de M5 (Agenda) e M6 (Prontuário) e M7 (Financeiro
 * paciente). Quando esses módulos vierem, expandimos esse dashboard.
 *
 * **Padrão canônico** das páginas internas (`PageContainer` +
 * `PageIntroCard` + `PageSectionCard` via subcomponentes).
 */
export function ClinicDashboardPage() {
  const { t } = useT('clinics');
  const { user } = useSession();
  const clinicId = useCurrentClinicId();

  // Saudação personalizada — usa primeiro nome do user_metadata se
  // existir, senão email, senão genérico.
  const fullName = (user?.user_metadata?.['full_name'] as string | undefined) ?? '';
  const firstName = fullName.split(' ')[0] || user?.email?.split('@')[0] || '';

  return (
    <ClinicShell>
      <PageContainer>
        <PageIntroCard
          eyebrow={t('dashboard.eyebrow')}
          title={
            firstName ? t('dashboard.titleWithName', { name: firstName }) : t('dashboard.title')
          }
          description={t('dashboard.description')}
          meta={<Badge variant="outline">{t('dashboard.metaBadge')}</Badge>}
        />

        {clinicId ? <PlanStatusSummaryCard clinicId={clinicId} /> : null}

        <QuickActionsCard />
      </PageContainer>
    </ClinicShell>
  );
}
